import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import moment from "moment";

import StepIndicator from "../../Components/Others/step-indicator";
import TextField from "../../Components/Form Fields/text-field";
import SSNField from "../../Components/Form Fields/ssn-field";
import DatePickerField from "../../Components/Form Fields/date-picker-field";
import SelectField from "../../Components/Form Fields/select-field";
import AddressAutoCompleteField from "../../Components/Form Fields/address-autocomplete-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import { RegularAmountField } from "../../Components/Form Fields/amount-field";

import renderStakeSaverSetupSuccess from "../../Organisms/Modals/Stake Saver/stake-saver-setup-success";
import renderSetupStakePay2 from "../../Organisms/Modals/Stake Pay 2/setup-stake-pay-2";
import renderFinishStakeSaverSetup from "../../Organisms/Modals/Stake Saver/finish-stake-saver-setup";
import { getAgreementLabels } from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";
import CheckBox from "../../Components/Form Fields/checkbox";
import EsignDisclosureViewer, { ESIGN_DISCLOSURE_PDF_URL } from "../../Components/Others/esign-disclosure-viewer";

import { useStakeDebit, useStakeSaverFormPrefill, useUserAccount } from "../../CustomHooks/Queries";
import { useApplyStakeChecking, useMigrateBankAccount, useSetupStakeSaver } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import withFormProvider from "../../HOC/withFormProvider";

import { APIhandler } from "../../Utils/api";
import { validation } from "../../Utils/validations";
import { getStakeDebitAPIObject } from "../../Utils/api-data-transforms";
import {
  dispatchAPIErrorToast,
  dispatchInfoToast,
  extract5DigitZipCode,
  formatCurrency,
  getFormattedSSN,
  getPublicIPAddress,
  getUserId,
  getUserSfid,
  isActiveUmfUserFromDealsResponse,
  mapSsoPrefillToStakeSaverFormPrefillShape,
  replaceNonNumericCharacters,
  transformCountryCodeFormatToUSNumber,
} from "../../Utils/helpers";
import { CountriesList, UnitOccupationList, USStates } from "../../Utils/constants";

import SideBar from "../../Organisms/App Utility Bar/sidebar";

import { StakeLogo } from "../../Assets/Images";
import { ArrowLeft, ChevronLeft, Lock } from "../../Assets/Icons";
import CloseButton from "../../Components/Buttons/close-button";

const IndustryList = [
  { label: "Technology", value: "technology" },
  { label: "Healthcare", value: "healthcare" },
  { label: "Finance", value: "finance" },
  { label: "Education", value: "education" },
  { label: "Retail", value: "retail" },
  { label: "Hospitality", value: "hospitality" },
  { label: "Manufacturing", value: "manufacturing" },
  { label: "Construction", value: "construction" },
  { label: "Transportation", value: "transportation" },
  { label: "Other", value: "other" },
];

const PersonalDetails = ({ showMigrationExtras = false }) => {
  const { register, watch, getFieldState, formState, setValue } = useFormContext();

  const email = watch("email");
  const phoneNumber = watch("phoneNumber");
  const occupation = watch("occupation");
  const citizenship = watch("citizenship");
  const industry = watch("industry");
  const address = watch("streetAddress") ?? "";
  const state = watch("state");

  const firstNameProps = register("firstName", {
    required: "First Name is required",
  });

  const lastNameProps = register("lastName", {
    required: "Last Name is required",
  });

  const occupationProps = register("occupation", {
    required: "Occupation is required",
  });

  const addressProps = register("streetAddress", {
    required: "Please enter your street address",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your address must not contain any special characters",
      hasAtleast3Characters: validation.streetAddress.hasAtleast3Characters,
    },
  });

  const aptSuiteUnitProps = register("aptSuiteUnit", {
    validate: {
      isAllowedCharacters: (aptSuiteUnit) =>
        !aptSuiteUnit ||
        validation.address.isAllowedCharacters(aptSuiteUnit) ||
        "Your apt/suite number must not contain any special characters",
      hasAtmost50Characters: validation.aptSuiteUnit.hasAtmost50Characters,
    },
  });

  const cityProps = register("city", {
    required: "Please enter your city",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your city must not contain any special characters",
      hasAtleast3Characters: validation.city.hasAtleast2Characters,
    },
  });

  const stateProps = register("state", {
    required: "Please choose your state from the list",
  });

  const zipCodeProps = register("zipCode", {
    required: "Please enter your ZIP code",
    validate: validation.zipcode,
  });

  const citizenshipProps = register("citizenship", {
    required: "Please select your citizenship",
  });

  const employerNameProps = register("employerName", {
    required: "Employer name is required",
    validate: {
      hasAtleast2Characters: (value) =>
        (value?.trim()?.length ?? 0) >= 2 || "Employer name must be at least 2 characters",
    },
  });

  const industryProps = register("industry", {
    validate: (value) => {
      if (!showMigrationExtras) return true;
      return IndustryList.some((option) => option.value === value) || "Please select an industry";
    },
  });

  const { error: firstNameFieldError } = getFieldState(firstNameProps.name, formState);
  const { error: lastNameFieldError } = getFieldState(lastNameProps.name, formState);
  const { error: occupationFieldError } = getFieldState(occupationProps.name, formState);
  const { error: addressFieldError } = getFieldState(addressProps.name, formState);
  const { error: aptSuiteUnitFieldError } = getFieldState(aptSuiteUnitProps.name, formState);
  const { error: cityFieldError } = getFieldState(cityProps.name, formState);
  const { error: stateFieldError } = getFieldState(stateProps.name, formState);
  const { error: zipCodeFieldError } = getFieldState(zipCodeProps.name, formState);
  const { error: citizenshipFieldError } = getFieldState(citizenshipProps.name, formState);
  const { error: employerNameFieldError } = getFieldState(employerNameProps.name, formState);
  const { error: industryFieldError } = getFieldState(industryProps.name, formState);

  return (
    <>
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="First Name"
            {...firstNameProps}
            errorMessage={firstNameFieldError?.message}
            error={!!firstNameFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="Last Name"
            {...lastNameProps}
            errorMessage={lastNameFieldError?.message}
            error={!!lastNameFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField className="filled" label="Email" value={email} disabled={true} />
        </Col>
        <Col sm={6} xs={12}>
          <TextField className="filled" label="Phone Number" value={phoneNumber} disabled={true} />
        </Col>
        <Col sm={6} xs={12}>
          <DatePickerField
            variant="filled"
            name="dob"
            label="Date of Birth"
            textFieldProps={{ placeholder: "", fullWidth: true }}
            validations={{
              isAValidDate: validation.date.isAValidDate,
              isAbove18YearsOfAge: validation.date.isAbove18YearsOfAge,
            }}
            datePickerProps={{ disableFuture: true }}
            required="Date of Birth is required"
          />
        </Col>
        <Col sm={6} xs={12}>
          <SSNField className="filled" required="Social Security Number is required" />
        </Col>
        <Col xs={12}>
          <SelectField
            className="filled"
            label="Occupation"
            options={UnitOccupationList}
            {...occupationProps}
            value={occupation ?? ""}
            errorMessage={occupationFieldError?.message}
          />
        </Col>
      </Row>
      <AddressAutoCompleteField
        fieldProps={{
          label: "Street Address",
          placeholder: "Enter a location",
          ...addressProps,
          className: "filled",
          value: address,
          errorMessage: addressFieldError?.message,
          error: !!addressFieldError,
        }}
        onAddressSelect={(selectedAddress) => {
          setValue("streetAddress", selectedAddress.address);
          setValue("city", selectedAddress.city);
          setValue("state", selectedAddress.state);
          setValue("zipCode", selectedAddress.zipCode);
        }}
      />
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label={
              <>
                Apt, Suite, Unit <span className="text-sm-medium color--american-silver">(Optional)</span>
              </>
            }
            {...aptSuiteUnitProps}
            errorMessage={aptSuiteUnitFieldError?.message}
            error={!!aptSuiteUnitFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="City"
            {...cityProps}
            errorMessage={cityFieldError?.message}
            error={!!cityFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <SelectField
            className="filled"
            label="State/Province"
            options={USStates}
            {...stateProps}
            value={state ?? ""}
            errorMessage={stateFieldError?.message}
            error={!!stateFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="Zip Code"
            {...zipCodeProps}
            errorMessage={zipCodeFieldError?.message}
            error={!!zipCodeFieldError}
          />
        </Col>
      </Row>
      {showMigrationExtras && (
        <>
          <Row className="row-gap-5">
            <Col xs={12}>
              <SelectField
                className="filled"
                label="Citizenship"
                options={CountriesList}
                {...citizenshipProps}
                value={citizenship ?? ""}
                errorMessage={citizenshipFieldError?.message}
                error={!!citizenshipFieldError}
              />
            </Col>
            <Col xs={12}>
              <TextField
                className="filled"
                label="Employer Name"
                {...employerNameProps}
                errorMessage={employerNameFieldError?.message}
                error={!!employerNameFieldError}
              />
            </Col>
            <Col xs={12}>
              <SelectField
                className="filled"
                label="Industry*"
                options={IndustryList}
                {...industryProps}
                value={industry ?? ""}
                errorMessage={industryFieldError?.message}
                error={!!industryFieldError}
              />
            </Col>
            <Col sm={6} xs={12}>
              <RegularAmountField
                name="monthlyDeposits"
                textFieldProps={{ className: "filled", placeholder: "0.00", label: "Monthly Deposits*" }}
                rules={{ required: "Monthly deposits amount is required" }}
              />
            </Col>
            <Col sm={6} xs={12}>
              <RegularAmountField
                name="monthlyWithdrawals"
                textFieldProps={{ className: "filled", placeholder: "0.00", label: "Monthly Withdrawals*" }}
                rules={{ required: "Monthly withdrawals amount is required" }}
              />
            </Col>
          </Row>
          <p className="migration-estimate-helper">
            * We&apos;ve set these to 2x your rent. Adjust if you expect different usage.
          </p>
        </>
      )}
    </>
  );
};

const Enrollment = () => {
  const { register, getFieldState, getValues, formState } = useFormContext();

  const { data: userAccountData } = useUserAccount();

  const { isLoading: isStakeSaverFormPrefillLoading, data: stakeSaverFormPrefill } = useStakeSaverFormPrefill();

  const nameOfTheUser = useMemo(
    () => `${userAccountData?.data?.data?.user?.firstName} ${userAccountData?.data?.data?.user?.lastName}`,
    [userAccountData?.data?.data?.user],
  );

  const signProps = register("sign", {
    required: "Please enter your signature",
    validate: (sign) =>
      sign?.trim() === nameOfTheUser?.trim() || "Please make sure the signature matches your full legal name",
  });

  const { error: signFieldError } = getFieldState(signProps.name, formState);

  return isStakeSaverFormPrefillLoading ? (
    <div className="loading-content">
      <CircularProgress />
    </div>
  ) : (
    <>
      {stakeSaverFormPrefill?.is_out_of_network_user && (
        <Row className="row-gap-sm-5 row-gap-4">
          <Col xs={12}>
            <RegularAmountField
              name="rentAmount"
              textFieldProps={{ className: "filled", placeholder: "1000.00" }}
              rules={{
                required: "Please enter your base rent",
              }}
              disabled={!!stakeSaverFormPrefill?.prefill_info?.rent_amount}
            />
          </Col>
          <Col sm={6} xs={12}>
            <DatePickerField
              name="leaseStartDate"
              label="Lease Start Date"
              textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
              required="Please pick a lease start date"
              validations={{
                isAValidDate: validation.date.isAValidDate,
              }}
              variant="filled"
              format="MM / DD / YYYY"
              disabled={!!stakeSaverFormPrefill?.prefill_info?.lease_start_date}
            />
          </Col>
          <Col sm={6} xs={12}>
            <DatePickerField
              name="leaseEndDate"
              label="Lease End Date"
              textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
              datePickerProps={{ disablePast: true }}
              required="Please pick a lease end date"
              validations={{
                isAValidDate: validation.date.isAValidDate,
                isGreaterThanLeaseStartDate: (leaseEndDate) =>
                  moment(leaseEndDate) > moment(getValues("leaseStartDate")) ||
                  "Lease end date must be after the start date",
              }}
              variant="filled"
              format="MM / DD / YYYY"
              disabled={!!stakeSaverFormPrefill?.prefill_info?.lease_end_date}
            />
          </Col>
        </Row>
      )}
      <TextField
        {...signProps}
        className="filled"
        label="Signature"
        description="Type your full legal name as your digital signature."
        placeholder={`Sign here as "${nameOfTheUser}"`}
        inputProps={{ style: { fontFamily: "Playball", fontSize: "22px", lineHeight: "32px" } }}
        error={!!signFieldError}
        errorMessage={signFieldError?.message}
      />
      <div className="form-disclaimers">
        <p className="form-acknowledgement mb-2">{stakeSaverFormPrefill?.descriptions?.signature_acknowledgement}</p>
        <p className="form-disclaimer">{stakeSaverFormPrefill?.descriptions?.offer_disclaimer}</p>
      </div>
      <div className="flex-grow-1"></div>
    </>
  );
};

const AGREEMENT_FIELD_NAMES = ["eSign", "agreeToTerms", "disclosures"];

const PERSONAL_DETAIL_FIELDS = [
  "firstName",
  "lastName",
  "dob",
  "ssn",
  "occupation",
  "streetAddress",
  "city",
  "state",
  "zipCode",
];

const MIGRATION_EXTRA_FIELDS = ["citizenship", "employerName", "industry", "monthlyDeposits", "monthlyWithdrawals"];

const getValidationFieldsForStep = (step, { setupStakeSaver, applyStakeChecking, bankMigrationDetails, isOutOfNetwork }) => {
  if (applyStakeChecking || bankMigrationDetails) {
    return step === 1
      ? bankMigrationDetails
        ? [...PERSONAL_DETAIL_FIELDS, ...MIGRATION_EXTRA_FIELDS]
        : PERSONAL_DETAIL_FIELDS
      : AGREEMENT_FIELD_NAMES;
  }

  if (setupStakeSaver) {
    if (step === 1) return PERSONAL_DETAIL_FIELDS;
    if (step === 2) {
      const enrollmentFields = ["sign"];
      if (isOutOfNetwork) {
        enrollmentFields.push("rentAmount", "leaseStartDate", "leaseEndDate");
      }
      return enrollmentFields;
    }
    return AGREEMENT_FIELD_NAMES;
  }

  return PERSONAL_DETAIL_FIELDS;
};

const Disclosures = ({ forceSyncteraDisclosures = false }) => {
  const [enableEsign, setEnableEsign] = useState(false);
  const { data: stakeDebit } = useStakeDebit();
  const isSyncteraUser = forceSyncteraDisclosures || (stakeDebit?.data?.data?.is_synctera_user ?? false);
  const agreementLabels = useMemo(() => getAgreementLabels(isSyncteraUser), [isSyncteraUser]);

  const { watch, register, getFieldState, formState, setValue } = useFormContext();

  const agreementFieldProps = AGREEMENT_FIELD_NAMES.map((fieldName) =>
    register(fieldName, {
      required: true,
    }),
  );
  const agreementValues = AGREEMENT_FIELD_NAMES.map((fieldName) => watch(fieldName) ?? false);
  const agreementFieldErrors = agreementFieldProps.map(({ name }) => getFieldState(name, formState).error);
  const eSignChecked = agreementValues[0];

  const handlePageChange = (e) => {
    if (e.currentPage === e.doc.numPages - 1) {
      setEnableEsign(true);
    }
  };

  const handleESignChange = (_event, checked) => {
    if (!enableEsign) return;
    setValue("eSign", checked, { shouldValidate: true });
    if (!checked) {
      setValue("agreeToTerms", false, { shouldValidate: true });
      setValue("disclosures", false, { shouldValidate: true });
    }
  };

  const handleSecondaryChange = (fieldName, checked) => {
    if (!eSignChecked) return;
    setValue(fieldName, checked, { shouldValidate: true });
  };

  return (
    <div className="d-flex flex-column row-gap-4">
      <div className="d-flex flex-column row-gap-2">
        <label className="text-sm-medium color--mist mb-0">E-Sign Consent Disclosure</label>
        <p className="text-sm-regular color--american-silver mb-0">
          Please review the E-Sign Consent Disclosure below. You’ll need to scroll to the bottom before you can
          continue.
        </p>
      </div>
      <EsignDisclosureViewer onPageChange={handlePageChange} />
      <div className="d-flex flex-column row-gap-1">
        <div className="d-flex align-items-start gap-2">
          <CheckBox
            {...agreementFieldProps[0]}
            checked={eSignChecked}
            disabled={!enableEsign}
            onChange={handleESignChange}
            ariaLabelledBy="sso-esign-label"
          />
          <p className="text-base-regular color--mist mb-0" id="sso-esign-label">
            I agree to receive disclosures and communications electronically.
          </p>
        </div>
        <div
          className={`d-flex flex-column row-gap-1 disclosures-secondary${eSignChecked ? "" : " disclosures-secondary--locked"}`}
        >
          {agreementLabels.map((label, i) => {
            const fieldName = AGREEMENT_FIELD_NAMES[i + 1];
            const fieldProps = agreementFieldProps[i + 1];
            const checked = agreementValues[i + 1];
            return (
              <div
                key={fieldName}
                className="d-flex align-items-start gap-2"
                onClick={(e) => {
                  if (e.target.closest("a")) return;
                  if (!eSignChecked) return;
                  handleSecondaryChange(fieldName, !checked);
                }}
              >
                <CheckBox
                  {...fieldProps}
                  checked={checked}
                  disabled={!eSignChecked}
                  onChange={(_event, nextChecked) => handleSecondaryChange(fieldName, nextChecked)}
                  ariaLabelledBy={`sso-agreement-label-${i}`}
                />
                <p className="text-base-regular color--mist mb-0" id={`sso-agreement-label-${i}`}>
                  {label}
                </p>
              </div>
            );
          })}
        </div>
        {agreementFieldErrors.some(Boolean) && (
          <span className="text-xs-regular color--error-red">Please agree to all the terms and conditions above</span>
        )}
      </div>
    </div>
  );
};

function SSO({ setupStakeSaver = false, applyStakeChecking = false, bankMigrationDetails = false }) {
  const [step, setStep] = useState(1);

  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { state: locationState } = useLocation();

  const { watch, trigger } = useFormContext();

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const occupation = watch("occupation");
  const address = watch("streetAddress");
  const aptSuiteUnit = watch("aptSuiteUnit");
  const city = watch("city");
  const state = watch("state");
  const zipCode = watch("zipCode");
  const dob = watch("dob");
  const ssn = watch("ssn");
  const leaseStartDate = watch("leaseStartDate");
  const leaseEndDate = watch("leaseEndDate");
  const rentAmount = watch("rentAmount");
  const citizenship = watch("citizenship");
  const employerName = watch("employerName");
  const industry = watch("industry");
  const monthlyDeposits = watch("monthlyDeposits");
  const monthlyWithdrawals = watch("monthlyWithdrawals");

  const stepLabels = useMemo(() => {
    if (setupStakeSaver) {
      return ["Personal details", "Enrollment", "Disclosures"];
    }
    if (applyStakeChecking || bankMigrationDetails) {
      return ["Personal details", "Disclosures"];
    }
    return [];
  }, [setupStakeSaver, applyStakeChecking, bankMigrationDetails]);

  //loading stake debit to load query function
  useStakeDebit();

  const { isLoading: isLoadingStakeSaverFormPrefill, data: stakeSaverFormPrefill } = useStakeSaverFormPrefill();
  const isOutOfNetworkUser = stakeSaverFormPrefill?.is_out_of_network_user ?? false;

  const { isLoading: isSettingUpStakeSaver, mutate: SetupStakeSaver } = useSetupStakeSaver({
    onSuccess: ({ data }) => {
      if (!data?.data?.is_kyc_approved) {
        renderFinishStakeSaverSetup(modalContext, { setupStakeSaverData: data?.data });
      } else {
        if (data?.data?.show_alert_message) {
          dispatchInfoToast(data?.data?.alert_message, { key: "setupSatkeSaver" });
        }
        renderStakeSaverSetupSuccess(modalContext);
      }
      navigate("/deals");
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "setupStakeSaver" }),
  });

  const { isLoading: isApplyingStakeChecking, mutate: ApplyStakeChecking } = useApplyStakeChecking({
    onSuccess: async ({ data }) => {
      if (locationState?.openStakePay2Modal) {
        renderSetupStakePay2(modalContext);
      }
      stakeCheckingStatusAction(getStakeDebitAPIObject(data?.data?.stake_checking_status));
      navigate(locationState?.previousPathname || -1);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "applyStakeChecking" }),
  });

  const { isLoading: isMigratingBankAccount, mutate: MigrateBankAccount } = useMigrateBankAccount({
    onSuccess: () => {
      navigate("/bank-migration-setting-up", {
        state: { nextPathname: locationState?.nextPathname || "/home" },
      });
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "migrateBankAccount" }),
  });

  const onSubmit = async () => {
    if (bankMigrationDetails && step === 2) {
      MigrateBankAccount({
        mobile_user_id: getUserId(),  
        firstname: firstName,
        lastname: lastName,
        address1: address,
        city,
        state,
        zipcode: zipCode,
        ssn: replaceNonNumericCharacters(ssn),
        dob,
        occupation,
        aprt_or_unit: aptSuiteUnit,
        citizenship: CountriesList.find((country) => country.value === citizenship)?.label ?? citizenship,
        employer_name: employerName,
        industry,
        monthly_deposits: monthlyDeposits?.unmasked,
        monthly_withdrawals: monthlyWithdrawals?.unmasked,
      });
    } else if (bankMigrationDetails && step === 1) {
      setStep(2);
    } else if (step === 3 && setupStakeSaver) {
      SetupStakeSaver({
        sfid: getUserSfid(),
        firstname: firstName,
        lastname: lastName,
        address1: address,
        city,
        state,
        zipcode: zipCode,
        mobile_user_id: getUserId(),
        user_ip: await getPublicIPAddress(),
        ssn: replaceNonNumericCharacters(ssn),
        dob,
        occupation,
        aprt_or_unit: aptSuiteUnit,
        lease_start_date: leaseStartDate,
        lease_end_date: leaseEndDate,
        rent_amount: rentAmount?.unmasked,
      });
    } else if (step === 2 && applyStakeChecking) {
      ApplyStakeChecking({
        firstname: firstName,
        lastname: lastName,
        address1: address,
        city: city,
        state: state,
        zipcode: zipCode,
        ssn: replaceNonNumericCharacters(ssn),
        dob,
        occupation,
        aprt_or_unit: aptSuiteUnit,
      });
    } else {
      setStep((p) => p + 1);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const fieldsToValidate = getValidationFieldsForStep(step, {
      setupStakeSaver,
      applyStakeChecking,
      bankMigrationDetails,
      isOutOfNetwork: isOutOfNetworkUser,
    });
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;
    await onSubmit();
  };

  const isMutating = isSettingUpStakeSaver || isApplyingStakeChecking || isMigratingBankAccount;

  const pageTitle = applyStakeChecking || bankMigrationDetails ? "Apply for Stake Debit" : "Let’s Set You Up";

  if (applyStakeChecking || bankMigrationDetails) {
    return (
      <div className="app-utility-container apply-stake-checking">
        <SideBar />
        <div className="asc-main">
          {isLoadingStakeSaverFormPrefill ? (
            <div className="loading-content">
              <CircularProgress />
            </div>
          ) : (
            <div className="asc-content">
              <div className="asc-header-row">
                <button className="asc-back-button" type="button" onClick={() => navigate(-1)}>
                  <ChevronLeft />
                </button>
                <h4 className="asc-title">{pageTitle}</h4>
              </div>
              <p className="asc-description">We’ve pre-filled your info — make sure it’s all correct.</p>
              <form className="asc-form-card" onSubmit={handleFormSubmit} autoComplete="off">
                <div className="asc-tabs" role="group" aria-label={`Step ${step} of 2`}>
                  <button
                    type="button"
                    className={`asc-tab${step === 1 ? " active" : ""}`}
                    onClick={() => step !== 1 && setStep(1)}
                    aria-current={step === 1 ? "step" : undefined}
                  >
                    Personal details
                  </button>
                  <button
                    type="button"
                    className={`asc-tab${step === 2 ? " active" : ""}`}
                    onClick={() => step !== 2 && handleFormSubmit({ preventDefault: () => {} })}
                    aria-current={step === 2 ? "step" : undefined}
                  >
                    Disclosures
                  </button>
                </div>
                {step === 1 ? (
                  <PersonalDetails showMigrationExtras={bankMigrationDetails} />
                ) : (
                  <Disclosures forceSyncteraDisclosures={bankMigrationDetails} />
                )}
                <PrimaryButton className="flex-grow-1" type="submit" disabled={isMutating}>
                  Continue {isMutating && <CircularProgress />}
                </PrimaryButton>
              </form>
              <div className="asc-secure-label">
                <Lock />
                Your personal data is encrypted and stored securely
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page sso-page">
      {isLoadingStakeSaverFormPrefill ? (
        <div className="onboarding-page loading-content">
          <CircularProgress />
        </div>
      ) : (
        <>
          <header className="bordered">
            <StakeLogo className="stake-logo" />
            <CloseButton onClick={navigate.bind(this, -1)} className="back-button" ariaLabel="Go back" />
          </header>
          <div className="max-width-container">
            <h4 className="page-title">{pageTitle}</h4>
            <p className="page-description">We’ve pre-filled your info — make sure it’s all correct.</p>
            <form className="registration-form" onSubmit={handleFormSubmit} autoComplete="off">
              <StepIndicator activeStep={step} hasLabels={true} steps={stepLabels.length} stepLabels={stepLabels} />
              {step === 1 ? (
                <PersonalDetails />
              ) : step === 2 ? (
                <Enrollment />
              ) : step === 3 ? (
                <Disclosures />
              ) : (
                <></>
              )}
              <div className="d-flex align-items-center column-gap-4">
                {step > 1 && (
                  <button className="back-button" type="button" onClick={setStep.bind(this, (p) => p - 1)} aria-label="Go back">
                    <ArrowLeft />
                  </button>
                )}
                <PrimaryButton className="flex-grow-1" type="submit" disabled={isMutating}>
                  Continue {isMutating && <CircularProgress />}
                </PrimaryButton>
              </div>
              <div className="data-secure-label">
                <Lock />
                Your personal data is encrypted and stored securely
              </div>
            </form>
            <div className="data-secure-label mt-4 mb-7">
              <Lock />
              Your personal data is encrypted and stored securely
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SSOWithFormProvider = withFormProvider(SSO, {
  defaultValues: async () => {
    try {
      const prefillRes = await APIhandler.sso.getPrefill();
      const mapped = mapSsoPrefillToStakeSaverFormPrefillShape(prefillRes?.data?.data);
      const prefillInfo = mapped?.prefill_info;
      const isOutOfNetworkUser = mapped?.is_out_of_network_user;

      // Default to not prefilling the address if we can't confirm the user isn't UMF.
      let isUmfUser = true;
      try {
        const dealsRes = await APIhandler.deals.requestDeals();
        isUmfUser = isActiveUmfUserFromDealsResponse(dealsRes);
      } catch (e) {
        // keep the safe default
      }
      const shouldPrefillAddress = !isUmfUser;
      const rent = Number(prefillInfo?.rent_amount ?? 0);
      const estimatedMonthlyUsage =
        rent > 0
          ? rent * 2
          : Number(prefillInfo?.expected_monthly_deposit ?? 0) ||
            Number(prefillInfo?.expected_monthly_withdrawal ?? 0);

      return {
        firstName: prefillInfo?.first_name ?? "",
        lastName: prefillInfo?.last_name ?? "",
        phoneNumber: transformCountryCodeFormatToUSNumber(prefillInfo?.phone ?? ""),
        email: prefillInfo?.email ?? "",
        streetAddress: shouldPrefillAddress ? (prefillInfo?.address1 ?? "") : "",
        aptSuiteUnit: shouldPrefillAddress ? (prefillInfo?.address2 ?? "") : "",
        city: shouldPrefillAddress ? (prefillInfo?.city ?? "") : "",
        state: shouldPrefillAddress ? (prefillInfo?.state ?? "") : "",
        zipCode: shouldPrefillAddress ? extract5DigitZipCode(prefillInfo?.zipcode) : "",
        dob: !!prefillInfo?.dob ? moment(prefillInfo?.dob).format("YYYY-MM-DD") : "",
        ssn: !!prefillInfo?.ssn ? getFormattedSSN(prefillInfo?.ssn) : "",
        occupation: prefillInfo?.occupation,
        citizenship: prefillInfo?.citizenship ?? "US",
        employerName: prefillInfo?.employer_name ?? "",
        industry:
          IndustryList.find(
            (option) => option.value === prefillInfo?.industry || option.label === prefillInfo?.industry,
          )?.value ?? "",
        eSign: false,
        agreeToTerms: false,
        disclosures: false,
        monthlyDeposits:
          estimatedMonthlyUsage > 0
            ? {
                masked: formatCurrency(estimatedMonthlyUsage)?.replace("$", ""),
                unmasked: String(estimatedMonthlyUsage),
              }
            : undefined,
        monthlyWithdrawals:
          estimatedMonthlyUsage > 0
            ? {
                masked: formatCurrency(estimatedMonthlyUsage)?.replace("$", ""),
                unmasked: String(estimatedMonthlyUsage),
              }
            : undefined,
        ...(isOutOfNetworkUser
          ? {
              leaseStartDate: !!prefillInfo?.lease_start_date
                ? moment(prefillInfo?.lease_start_date).format("YYYY-MM-DD")
                : "",
              leaseEndDate: !!prefillInfo?.lease_end_date
                ? moment(prefillInfo?.lease_end_date).format("YYYY-MM-DD")
                : "",
              rentAmount: !!prefillInfo?.rent_amount
                ? {
                    masked: formatCurrency(prefillInfo?.rent_amount)?.replace("$", ""),
                    unmasked: String(prefillInfo?.rent_amount),
                  }
                : undefined,
            }
          : {}),
      };
    } catch (e) {
      return {};
    }
  },
});

export default SSOWithFormProvider;
